'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/context/LanguageContext';
import { useUnit } from '@/context/UnitContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap, faInfoCircle, faChevronDown, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

import { siteConfig } from '@/config/site';
import { SlideItem } from '@/types/slide';
import { ApiResponse } from '@/types/api';

interface Slide {
  id: number;
  title: string;
  subtitle: string;
  image: string;
}

export default function HeroSection() {
  const { t, language } = useTranslation();
  const { unitId, unitQueryParams } = useUnit();
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Fallback slide from site config
  const fallbackSlide: Slide = {
    id: 0,
    title: siteConfig.title,
    subtitle: siteConfig.description,
    image: '/img/bg/1.webp',
  };

  // Fetch slides from API
  useEffect(() => {
    if (!unitId && !unitQueryParams) {
      setLoading(false);
      return;
    }

    const fetchSlides = async () => {
      try {
        const params = unitQueryParams || (unitId ? `unit_id=${unitId}` : '');
        const response = await axios.post<ApiResponse<SlideItem[]>>('/api/doServices', {
          method: 'GET',
          endpoint: `web/slide?limit=10&page=1&${params}&order=orders ASC`
        });

        if (response.data?.data && response.data.data.length > 0) {
          const mapped: Slide[] = response.data.data.map((item) => ({
            id: item.id,
            title: language === 'id' ? (item.title_idn || item.title_en) : (item.title_en || item.title_idn),
            subtitle: language === 'id' ? (item.subtitle_idn || item.subtitle_en) : (item.subtitle_en || item.subtitle_idn),
            image: `/api/doServicesProxy?endpoint=web/file/slide/${item.id}&isImage=true`,
          }));
          setSlides(mapped);
        }
      } catch (error) {
        console.error('Error fetching slides:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, [unitId, unitQueryParams, language]);

  // Use fallback if no slides
  const displaySlides = slides.length > 0 ? slides : [fallbackSlide];

  // Auto-advance slides
  useEffect(() => {
    if (displaySlides.length <= 1) return;

    const interval = setInterval(() => {
      goToNextSlide();
    }, 6000); // 6 seconds per slide

    return () => clearInterval(interval);
  }, [displaySlides.length, currentSlide]);

  const goToNextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % displaySlides.length);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [displaySlides.length, isTransitioning]);

  const goToPrevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + displaySlides.length) % displaySlides.length);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [displaySlides.length, isTransitioning]);

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const current = displaySlides[currentSlide];

  // Split title for visual effect
  const titleParts = current.title.includes('-') ? current.title.split('-') : [current.title];
  const mainTitle1 = titleParts[0].trim();
  const mainTitle2 = titleParts.slice(1).join(' ').trim();

  return (
    <header id="home" className="relative h-screen flex flex-col items-center justify-center text-center text-white overflow-hidden">
      {/* Slides Background */}
      {displaySlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === currentSlide ? 'opacity-100 z-0' : 'opacity-0 z-[-1]'
          }`}
        >
          <Image
            src={slide.image}
            alt={slide.title}
            fill
            priority={index === 0}
            className="object-cover"
            sizes="100vw"
            onError={(e) => {
              // Fallback to default image on error
              (e.target as HTMLImageElement).src = '/img/bg/1.webp';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-unmulDarkGreen/60 via-unmulDarkGreen/50 to-unmulDarkGreen/90"></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 px-6 max-w-6xl mx-auto space-y-8" data-aos="fade-up" data-aos-duration="1000">
        {/* Main Heading - Animated on slide change */}
        <h1
          key={currentSlide}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight font-serif drop-shadow-lg flex flex-col items-center animate-fade-in-up"
        >
          <span>{mainTitle1}</span>
          {mainTitle2 && (
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-unmulYellow via-yellow-200 to-white mt-2 block">
              {mainTitle2}
            </span>
          )}
        </h1>

        {/* Subtitle - Animated */}
        <p
          key={`subtitle-${currentSlide}`}
          className="text-base sm:text-lg md:text-2xl text-gray-100 max-w-3xl mx-auto font-light leading-relaxed drop-shadow-md animate-fade-in"
        >
          {current.subtitle}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
          <Link
            href="#admission"
            className="px-8 py-4 bg-unmulYellow text-unmulDarkGreen font-bold rounded-lg text-lg hover:bg-white transition shadow-xl transform hover:scale-105 flex items-center justify-center group"
          >
            <FontAwesomeIcon icon={faGraduationCap} className="mr-3 group-hover:rotate-12 transition" />
            {t('hero.cta.register')}
          </Link>
          <Link
            href="#profil"
            className="px-8 py-4 bg-white/10 border-2 border-white/30 backdrop-blur-sm text-white font-bold rounded-lg text-lg hover:bg-white/20 hover:border-white transition flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faInfoCircle} className="mr-3" />
            {language === 'id' ? 'Jelajahi' : 'Explore'}
          </Link>
        </div>
      </div>

      {/* Slide Navigation Arrows */}
      {displaySlides.length > 1 && (
        <>
          <button
            onClick={goToPrevSlide}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-black/30 hover:bg-unmulGreen text-white rounded-full flex items-center justify-center transition backdrop-blur-sm group"
            aria-label="Previous slide"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-xl group-hover:scale-110 transition" />
          </button>
          <button
            onClick={goToNextSlide}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-black/30 hover:bg-unmulGreen text-white rounded-full flex items-center justify-center transition backdrop-blur-sm group"
            aria-label="Next slide"
          >
            <FontAwesomeIcon icon={faChevronRight} className="text-xl group-hover:scale-110 transition" />
          </button>
        </>
      )}

      {/* Slide Indicators */}
      {displaySlides.length > 1 && (
        <div className="absolute bottom-24 z-20 flex gap-3">
          {displaySlides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide
                  ? 'w-10 h-3 bg-unmulYellow'
                  : 'w-3 h-3 bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 z-10 animate-bounce">
        <Link
          href="#stats"
          className="text-white/70 hover:text-unmulYellow transition text-3xl cursor-pointer"
          aria-label="Scroll to stats section"
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </Link>
      </div>



      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.7s ease-out forwards;
        }
        .animate-fade-in {
          animation: fadeIn 0.7s ease-out forwards;
        }
      `}</style>
    </header>
  );
}
